'use client'

import React from 'react'

import {
  toast,
  ToastOptions,
  ToastContainer as WrappedToastContainer,
  type CloseButtonProps,
  type Id,
} from 'react-toastify'

import { isSystemError, isUserError } from 'shared/utils/errors'
import Icon, { IconName } from 'shared/timber/v1/Icon'
import Button from 'shared/timber/v1/Button'

// react-toastify@11 no longer ships an inject-style helper; the default
// animations (Toastify__trackProgress, Toastify__bounce-exit-*) live in this
// CSS file and the close pipeline depends on them firing animationend events.
import 'react-toastify/dist/ReactToastify.css'
import './Toast.scss'

export type ToastNotificationType =
  | 'none'
  | 'error'
  | 'info'
  | 'success'
  | 'warning'

const CloseButton = ({ closeToast }: CloseButtonProps) => {
  return (
    <Button
      iconOnly
      size="xs"
      appearance="tertiary"
      onClick={closeToast}
      aria-label="Close notification"
    >
      <Icon icon="x_mark" size="xs" />
    </Button>
  )
}

export const ToastContainer = ({ autoClose = 5000, closeButton = true }) => {
  return (
    <WrappedToastContainer
      icon={false}
      autoClose={autoClose}
      className="ToastContainer"
      closeButton={closeButton ? CloseButton : undefined}
      hideProgressBar
    />
  )
}

// to not interfere with intercom
const DEFAULT_POSITION = { position: 'top-right' as const }

export interface ToastNotificationProps {
  icon?: IconName
  type?: ToastNotificationType
  title?: string | React.ReactNode
  message?: string | React.ReactNode
  options?: ToastOptions
}

export const Message = ({
  icon,
  type,
  title,
  message,
}: ToastNotificationProps) => {
  return (
    <div className="ToastMessage">
      {icon && (
        <div className="ToastMessage__Icon">
          {<Icon icon={icon} background={type} />}
        </div>
      )}
      <div className="ToastMessage__Content">
        {title && <div className="ToastMessage__Title">{title}</div>}
        <div className="ToastMessage__Message">{message}</div>
      </div>
    </div>
  )
}

export function toastError(
  error: unknown,
  options?: Partial<Omit<ToastNotificationProps, 'type'>>,
) {
  const defaultErrorMessage = 'Something went wrong'
  let errorMessage = defaultErrorMessage

  if (isSystemError(error) && error.shouldNotifyUser()) {
    errorMessage = error.userMessage
  } else if (isUserError(error)) {
    errorMessage = error.message
  } else if (error instanceof Error) {
    errorMessage = error.message
  } else if (typeof error === 'string') {
    errorMessage = error
  }

  return toastNotification({
    type: 'error',
    title: options?.title || 'Error',
    message: errorMessage,
    icon: 'alert_triangle',
    ...options,
  })
}

export function toastSuccess(
  message: string,
  options?: Partial<Omit<ToastNotificationProps, 'type'>>,
) {
  return toastNotification({
    type: 'success',
    title: options?.title || 'Success',
    message: message,
    icon: 'check_circle',
    ...options,
  })
}

export function dismissToast(id: Id) {
  toast.dismiss(id)
}

export function toastNotification({
  icon,
  type = 'info',
  title,
  message,
  options = {},
}: ToastNotificationProps): Id {
  switch (type) {
    case 'info':
      return toast.info(Message({ icon, type, title, message }), {
        ...DEFAULT_POSITION,
        ...options,
      })
    case 'error':
      return toast.error(Message({ icon, type, title, message }), {
        ...DEFAULT_POSITION,
        ...options,
      })

    case 'success':
      return toast.success(Message({ icon, type, title, message }), {
        ...DEFAULT_POSITION,
        ...options,
      })

    case 'warning':
      return toast.warning(Message({ icon, type, title, message }), {
        ...DEFAULT_POSITION,
        ...options,
      })

    default:
      return toast.info(Message({ icon, type, title, message }), {
        ...DEFAULT_POSITION,
        ...options,
      })
  }
}
